<template>
  <Flex direction="column">
    <Flex
      align="end"
      justify="between"
      :direction="$screen === 's' ? 'column' : 'row'"
    >
      <Flex align="end">
        <BaseDropdown
          v-model="filteredCategories"
          :options="categoriesWithServices.map((c) => ({
          value: c.id,
          label: c.name
        }))"
          :label="$t('global.items.category', 2)"
          v-test="'service-price-update-filter'"
        />
        <BaseSearch
          v-model="searchQuery"
          v-test="'service-price-update-search'"
        />
      </Flex>
      <Flex align="end">
        <BaseInputPricePercentage
          v-model="allValue"
          v-model:type="allType"
          v-test="'service-price-update-all'"
        />
        <BaseButton
          color="inverted"
          :disabled="!allValue"
          @click="onAllClick"
          v-test="'service-price-update-all-submit'"
        >
          {{ $t('services.price_update.apply_to_all_services') }}
        </BaseButton>
      </Flex>
    </Flex>
    <BaseText
      v-if="!groupedTableData.length"
      center
    >
      {{ $t('global.no_results') }}
    </BaseText>
    <MobileServices
      v-else-if="groupedTableData.length && $screen === 's'"
      :groupedTableData="groupedTableData"
      :categoriesWithServices="categoriesWithServices"
      @onAction="onAction"
    />
    <BaseCard
      v-else
      v-for="category in groupedTableData"
      :key="category.categoryId"
      :heading="categoriesWithServices.find(c => c.id === category.categoryId)?.name"
      :notification="getChangedAmount(category.services)"
      collapsible
      v-test="'service-price-update-card'"
    >
      <BaseTable
        :headers="[$t('global.name'), {
          value: $t('services.price_update.current_price'),
          alignRight: true
        }, $t('services.price_update.new_price')]"
        :rows="category.services.map((service) => ({ 
          id: service.serviceId,
          cells: [
            {
              value: service.name,
              fullWidth: true
            }, 
            {
              value: filters.currency(service.originalPrice),
              alignRight: true
            }, 
            {
              value: service.newPrice,
              editable: true,
              type: 'currency',
              maxValue: 999999999,
            },
          ],
          actions: service.newPrice !== service.originalPrice ? [
            {
              name: 'undo',
              icon: 'undo',
              label: $t('global.actions.reset'),
            },
          ] : []
        }))"
        @valueChange="valueChange"
        @action="onAction"
        v-test="'service-price-update-list'"
      />
    </BaseCard>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { modal } from '@/helpers/ui';
import { useServiceGroupsStore } from '@/stores/service-groups';
import MobileServices from './MobileServices.vue';
import type { TableData } from './types';
import { getChangedAmount } from './utils';

enum InputType {
  Fixed = 'FIXED_AMOUNT',
  Perc = 'PERCENTAGE',
}

const props = defineProps<{
  modelValue: TableData[];
}>();
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const searchQuery = ref('');
const allValue = ref(0);
const allType = ref(InputType.Perc);

const { flattenedServices: services, categoriesWithServices } = storeToRefs(
  useServiceGroupsStore(),
);

const filteredCategories = ref<number[]>(
  categoriesWithServices.value.map((c) => c.id),
);

const serviceIdToCategoryId = computed<Map<number, number>>(
  () => new Map(services.value.map((s) => [s.id, s.category.id])),
);

const categoryOrder = computed(() => {
  const m = new Map<number, number>();
  categoriesWithServices.value.forEach((c, i) => {
    m.set(c.id, i);
  });
  return m;
});

const groupedTableData = computed<
  { categoryId: number; services: TableData[] }[]
>(() => {
  const groups = new Map<number, TableData[]>();

  for (const row of props.modelValue) {
    const categoryId = serviceIdToCategoryId.value.get(row.serviceId);
    if (categoryId == null) continue;
    if (!groups.has(categoryId)) groups.set(categoryId, []);
    groups.get(categoryId)!.push(row);
  }

  const filtered = Array.from(groups, ([categoryId, rows]) => ({
    categoryId,
    services: rows.filter((row) =>
      row.name.toLowerCase().includes(searchQuery.value.toLowerCase()),
    ),
  }))
    .filter((g) => g.services.length > 0)
    .filter((g) => filteredCategories.value.includes(g.categoryId));

  // Sort by the order of `categories`
  const order = categoryOrder.value;
  filtered.sort((a, b) => {
    const ai = order.get(a.categoryId);
    const bi = order.get(b.categoryId);
    // push unknown ids to the end, keep their relative order
    if (ai == null && bi == null) return 0;
    if (ai == null) return 1;
    if (bi == null) return -1;
    return ai - bi;
  });

  return filtered;
});

const valueChange = (value: { value: number; id: number }) => {
  const service = props.modelValue.find(
    (service) => service.serviceId === value.id,
  );
  if (service) {
    service.newPrice = value.value;
  }
};

const onAction = (value: { id: number; option: string }) => {
  if (value.option === 'undo') {
    const service = props.modelValue.find(
      (service) => service.serviceId === value.id,
    );
    if (service) {
      service.newPrice = service.originalPrice;
    }
  }
};

const onAllClick = () => {
  const visibleServices = props.modelValue.filter((service) =>
    groupedTableData.value.some((group) =>
      group.services.some((s) => s.serviceId === service.serviceId),
    ),
  );

  modal('confirmation', {
    message: t('services.price_update.apply_to_all_services_confirmation'),
  }).then(() => {
    visibleServices.forEach((service) => {
      service.newPrice =
        allType.value === InputType.Perc
          ? Math.round(service.originalPrice * (1 + allValue.value / 100))
          : service.originalPrice + allValue.value;
    });
  });
};
</script>
